import { uppercaseFirstLetter } from 'common/utils/stringUtil';
import { isEmpty } from 'lodash';
import {
  NUMERIC_SIM_PROFILE_IDS,
  SESSION_STATUS,
  SESSION_STATUS_NUMERIC,
  SimInventoryExtededFilterBody,
  SimInventoryExtededFilterBodyIdsProps,
  SimInventorySearchParamsV2,
  SimInventorySort,
  SimProfileId,
  SessionStatusFilter,
} from 'simInventory/models/SimProfile.model';
import { filterEmptyCustomFieldsValues } from 'src/admin/customFields/utils/customFieldHelpers';

const parseIdSerach = (
  searchText: string[],
  chosenIdType: SimProfileId,
  body: SimInventoryExtededFilterBody,
) => {
  if (searchText.length === 1) {
    const id = chosenIdType.toLowerCase() as SimInventoryExtededFilterBodyIdsProps;
    return { ...body, [id]: searchText[0] };
  } else if (searchText.length > 0 && searchText[0]) {
    return {
      ...body,
      simProfileIds: {
        type: NUMERIC_SIM_PROFILE_IDS[chosenIdType],
        ids: searchText,
      },
    };
  }

  return body;
};

const parseCustomFields = (
  customFields: Record<string, string | string[]> | undefined,
  body: SimInventoryExtededFilterBody,
) => {
  if (!customFields) {
    return body;
  }

  const parseCustomFieldToSearchParm = (id: string, value: string | string[]) => {
    if (Array.isArray(value)) {
      return { id, selectionOptionIds: value };
    }

    return { id, textValue: value };
  };

  const customFieldSearch = Object.entries(customFields)
    .filter(([, value]) => filterEmptyCustomFieldsValues(value))
    .map(([id, value]) => parseCustomFieldToSearchParm(id, value));

  return {
    ...body,
    customFieldSearch,
  };
};

const parseSessionStatus = (
  sessionStatus: SessionStatusFilter,
  body: SimInventoryExtededFilterBody,
) => {
  if (sessionStatus === SESSION_STATUS.UNKNOWN) {
    return body;
  }

  const sessionStatusValue =
    sessionStatus === SESSION_STATUS.IN_SESSION
      ? SESSION_STATUS_NUMERIC.IN_SESSION
      : SESSION_STATUS_NUMERIC.DISCONNECTED;

  return {
    ...body,
    sessionStatus: [sessionStatusValue],
  };
};

const parseAccounts = (accounts: (string | null)[], body: SimInventoryExtededFilterBody) => {
  if (isEmpty(accounts)) {
    return body;
  }

  const parsedAccounts = accounts.map((account) =>
    account === null ? account : Number.parseInt(account),
  );

  return {
    ...body,
    accountRef: parsedAccounts,
  };
};

export const prepareSimInventorySearchBody = (
  searchParams: SimInventorySearchParamsV2,
  sort: SimInventorySort | null = null,
) => {
  let body: SimInventoryExtededFilterBody = {};
  body = parseIdSerach(searchParams.searchText, searchParams.chosenIdType, body);
  body = parseCustomFields(searchParams.customFields, body);
  body = parseSessionStatus(searchParams.sessionStatus, body);
  body = parseAccounts(searchParams.accounts, body);

  if (searchParams.connectionId) {
    body['connectionId'] = searchParams.connectionId;
  }

  if (searchParams.orderNumber) {
    body['orderNumber'] = searchParams.orderNumber;
  }

  if (!isEmpty(searchParams.subscriptionStatus)) {
    body.subscriptionStatus = searchParams.subscriptionStatus;
  }

  if (!isEmpty(searchParams.productCodes)) {
    body.productCodes = searchParams.productCodes;
  }

  if (!isEmpty(searchParams.mnoStatus)) {
    body.mnoStatus = searchParams.mnoStatus;
  }

  if (!isEmpty(searchParams.tags)) {
    body.tags = searchParams.tags;
  }

  if (!isEmpty(searchParams.mobileNetworkOperator)) {
    body.cciis = searchParams.mobileNetworkOperator;
  }

  if (!isEmpty(searchParams.mnoNames)) {
    body.mnoNames = searchParams.mnoNames.map((name) => {
      return name === 'blank' ? null : name;
    });
  }

  if (sort) {
    body.order = `${uppercaseFirstLetter(sort.columnName)}:${sort.sort?.toUpperCase()}`;
  }

  if (searchParams.iccidFrom || searchParams.iccidTo) {
    body.simProfileIdsRange = {
      type: 0,
      idsFrom: searchParams.iccidFrom,
      idsTo: searchParams.iccidTo,
    };
  }

  return body;
};

export const preparePaginatedSimInventorySearchBody = (
  page: number,
  entryPerPage: number,
  searchParams: SimInventorySearchParamsV2,
  sort: SimInventorySort | null = null,
) => {
  const body = prepareSimInventorySearchBody(searchParams, sort);
  body.pageNumber = page + 1;
  body.pageSize = entryPerPage;

  return body;
};
